import { useEffect, useMemo, useState } from 'react';
import { MapPin, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { usePermission } from '@/auth';
import { useAuthStore } from '@/auth/auth-store';
import {
  useCreatePractice,
  usePractice,
  usePracticesList,
  useUpdatePractice,
} from '@/settings/services';
import {
  emptyDetail,
  normaliseDetail,
} from '@/settings/components/practice-edit/practice-edit-merge';
import { PracticeDetailsTab } from '@/settings/components/practice-edit/tabs/practice-details-tab';
import type { PracticeSetupDetail } from '@/types/settings-api';

/**
 * Onboarding wizard tab 1 — Add Practice Details (PROD-866, epic PROD-856).
 *
 * Flutter parity: `practice_setup_module/practice_setup/presentation/widgets/
 * practice/add_practice_details_widget.dart`. Lists existing practices and
 * opens a slide-in panel (Flutter `SlidingPopup`) for Add or Edit.
 *
 * Wizard Next gate: enables when `practices.length > 0`. Mirrors Flutter
 * `:314-320` — no hours/rooms check at the wizard level.
 *
 * Scope (per CONVERSION_NOTES + PROD-866 ticket): only practice-details
 * fields. Working hours + exam rooms (Flutter's sub-tab 1 inside the
 * Add popup) are deferred — users complete onboarding with name + license
 * and configure hours/rooms post-wizard from Settings → Practices.
 *
 * Reuses:
 *   - `usePracticesList` / `usePractice` / `useCreatePractice` / `useUpdatePractice`
 *   - `<PracticeDetailsTab>` (shared form from settings-module)
 *   - `emptyDetail` / `normaliseDetail` helpers
 *   - shadcn `Sheet` for the slide-in panel
 */
export interface PracticeStepProps {
  /** Called when at least one practice exists — gates the wizard's Next button. */
  enableNext: () => void;
}

type SheetState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; practiceSetupId: string };

export function PracticeStep({ enableNext }: PracticeStepProps) {
  const orgId = useAuthStore((s) => s.user?.organizationId);
  const { data: practices = [], isLoading } = usePracticesList(orgId, false);

  const [sheet, setSheet] = useState<SheetState>({ mode: 'closed' });

  // Flutter parity: Next button enables once at least one practice exists.
  // Practice list refetches on create/update success (RQ onSuccess invalidates
  // the `['settings', 'practices-list']` key — see use-practice.ts:47, 102).
  useEffect(() => {
    if (practices.length > 0) {
      enableNext();
    }
  }, [practices.length, enableNext]);

  return (
    <div className="mx-auto max-w-[720px] px-8 py-10">
      <h1 className="[font:var(--type-card-title)] text-[var(--ink-body)]">
        Add A Practice
      </h1>
      <p className="mt-4 [font:var(--type-body)] text-[var(--ink-meta)]">
        Add at least one practice. Once you're done we'll take it from there.
        <br />
        Fields marked with a red dot are mandatory.
      </p>

      <div className="mt-10 space-y-3">
        {isLoading ? (
          <div className="rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)] px-6 py-10 text-center [font:var(--type-body)] text-[var(--ink-meta)]">
            Loading practices…
          </div>
        ) : practices.length === 0 ? (
          <Button
            type="button"
            className="h-8"
            onClick={() => setSheet({ mode: 'create' })}
          >
            Add Practice
          </Button>
        ) : (
          <>
            {practices.map((p) => (
              <PracticeRow
                key={p.practiceSetupId ?? p.practiceName ?? Math.random()}
                practiceName={p.practiceName ?? ''}
                address={p.address ?? ''}
                isPrimary={p.isPrimary === true}
                onEdit={
                  p.practiceSetupId
                    ? () =>
                        setSheet({ mode: 'edit', practiceSetupId: p.practiceSetupId! })
                    : undefined
                }
              />
            ))}
            <button
              type="button"
              onClick={() => setSheet({ mode: 'create' })}
              className="block w-full rounded-card border border-dashed border-[var(--surface-line)] bg-[var(--surface-read)] px-4 py-4 text-center text-md font-semibold text-[var(--accent-primary)] transition-colors hover:bg-[var(--color-blue-100)]"
            >
              Add Another Practice
            </button>
          </>
        )}
      </div>

      <PracticeFormSheet
        state={sheet}
        orgId={orgId}
        onClose={() => setSheet({ mode: 'closed' })}
      />
    </div>
  );
}

function PracticeRow({
  practiceName,
  address,
  isPrimary,
  onEdit,
}: {
  practiceName: string;
  address: string;
  isPrimary: boolean;
  onEdit: (() => void) | undefined;
}) {
  return (
    <div className="flex items-center gap-6 rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)] px-6 py-4">
      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-card bg-[var(--color-blue-100)] text-[var(--accent-primary)]">
        <MapPin className="h-6 w-6" aria-hidden />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="[font:var(--type-card-title)] text-[var(--ink-body)]">
            {practiceName}
          </span>
          {isPrimary && (
            <span className="rounded bg-[var(--color-surface-background)] px-2 py-0.5 [font:var(--type-body)] text-[var(--ink-meta)]">
              Primary
            </span>
          )}
        </div>
        {address && (
          <div className="mt-2 flex items-center gap-2 [font:var(--type-body)] text-[var(--ink-meta)]">
            <MapPin className="h-4 w-4 text-[var(--accent-primary)]" aria-hidden />
            <span>{address}</span>
          </div>
        )}
      </div>
      {onEdit && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onEdit}
          className="gap-1"
        >
          <Pencil className="h-4 w-4" aria-hidden />
          Edit
        </Button>
      )}
    </div>
  );
}

function PracticeFormSheet({
  state,
  orgId,
  onClose,
}: {
  state: SheetState;
  orgId: string | null | undefined;
  onClose: () => void;
}) {
  const isOpen = state.mode !== 'closed';
  const isEdit = state.mode === 'edit';
  const editId = state.mode === 'edit' ? state.practiceSetupId : null;
  // Mirror settings/components/practice-edit/practice-edit-page.tsx:63-65 — the
  // add and edit halves of Manage_Practice_Details are separate grants, so the
  // sheet gates on whichever one it is actually performing. This previously
  // passed `canEdit={true}`, the same unconditional-true bypass PROD-1768 fixes
  // in roles-step.tsx.
  const canAddPractice = usePermission(['Manage_Practice_Details'], { add: true });
  const canEditPractice = usePermission(['Manage_Practice_Details'], { edit: true });
  const canEditDetails = isEdit ? canEditPractice : canAddPractice;

  const { data: editFetch, isLoading: isEditLoading } = usePractice(
    editId ?? undefined,
  );

  const createMutation = useCreatePractice();
  const updateMutation = useUpdatePractice();
  const isSaving = createMutation.isPending || updateMutation.isPending;

  const [draft, setDraft] = useState<PracticeSetupDetail | null>(null);

  // Reset / seed draft when sheet opens or edit-fetch lands.
  useEffect(() => {
    if (state.mode === 'create') {
      setDraft(emptyDetail('', orgId ?? ''));
      return;
    }
    if (state.mode === 'edit' && editFetch?.practiceSetupDetail) {
      setDraft(normaliseDetail(editFetch.practiceSetupDetail));
    }
    if (state.mode === 'closed') {
      setDraft(null);
    }
  }, [state.mode, editFetch, orgId]);

  const isValid = useMemo(
    () => !!draft && draft.practiceName.trim().length > 0,
    [draft],
  );

  const handleSave = () => {
    if (!draft) return;
    if (!isValid) {
      toast.error('Practice name is required.');
      return;
    }
    if (isEdit) {
      updateMutation.mutate(draft, {
        onSuccess: () => {
          toast.success(`Saved ${draft.practiceName}`);
          onClose();
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Save failed.'),
      });
    } else {
      createMutation.mutate(draft, {
        onSuccess: () => {
          toast.success(`Created ${draft.practiceName}`);
          onClose();
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Create failed.'),
      });
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full max-w-[720px] sm:max-w-[720px] overflow-y-auto scrollbar-hide">
        <SheetHeader className="border-b border-[var(--surface-line)] px-6 pb-4 pt-6">
          <SheetTitle>{isEdit ? 'Edit Practice' : 'Add Practice'}</SheetTitle>
          <SheetDescription>
            Working hours and exam rooms can be configured later from Settings → Practices.
          </SheetDescription>
        </SheetHeader>

        <div className="px-6 py-6">
          {isEdit && isEditLoading ? (
            <p className="[font:var(--type-body)] text-[var(--ink-meta)]">
              Loading practice…
            </p>
          ) : draft ? (
            <PracticeDetailsTab
              draft={draft}
              onChange={setDraft}
              canEdit={canEditDetails}
            />
          ) : null}
        </div>

        <div className="sticky bottom-0 flex justify-end gap-3 border-t border-[var(--surface-line)] bg-[var(--surface-read)] px-6 py-4">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!isValid || isSaving}
          >
            {isSaving ? 'Saving…' : isEdit ? 'Save' : 'Create Practice'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
